import { ToLogin } from "./ToLogin";
import { FC, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Navigate, useLocation } from "react-router-dom"
import { StateType } from "../backend/store"
import { billingTriggerActions } from "../backend/store/reducers/billingReducer"
import { lockedLandingPath } from "../next/routes/lockedLanding"
import { hasPermission } from "../utils/permissions"

type Props = {
  children: any
}

export const PrivateRoute: FC<Props> = ({ children }) => {
  const isUser = localStorage.getItem("user")
  const location = useLocation()
  const dispatch = useDispatch()

  const currentUser = useSelector((state: StateType) => state.authStore.currentUser);
  const billingStore = useSelector((state: StateType) => state.billingStore);

  const billingEnabled = billingStore.billingEnabled;
  const hasActiveSubscription = billingStore.hasActiveSubscription;
  const isTrialActive = billingStore.isTrialActive;
  const trialEndsAt = billingStore.trialEndsAt;
  const isSuperAdmin = currentUser?.role === "super_admin";

  // /billing/plans: a locked account must be able to reach Choose a plan (the Billing page's own button), not be sent back.
  // /help and /help/contact: while the account is locked the Help page still opens (it shows Contact us only; its tabs send the person back to it),
  // and /help/contact is the locked-account page itself (account holder's details, or the Owner's Plans and billing / Download / Contact us).
  const isAllowedPath = ["/pricing", "/billing", "/billing/plans", "/unAuthPage", "/account-suspended", "/package-pending", "/help", "/help/contact"].includes(location.pathname);

  // Pre-trial: trial has never been started (no data uploaded yet) — allow full access.
  // This mirrors the backend EnsureWorkspaceHasSubscription pre-trial bypass:
  //   if ($workspace->trial_ends_at === null && !$workspace->hasActiveSubscription()) allow through
  const isPreTrial = trialEndsAt === null && !hasActiveSubscription;

  const locked = !isSuperAdmin && billingEnabled && !hasActiveSubscription && !isTrialActive && !isPreTrial;
  const hasBillingPerm = hasPermission(['read_billing'], currentUser);

  // Walkthrough decision 7: the Owner of a locked account can still reach My settings (password, sign out everywhere, leave).
  // Decision 8: a Manager with the billing switch stays Billing-only, so this is the Owner alone.
  const isOwnerSettingsPath = !!billingStore.isWorkspaceOwner && location.pathname === "/settings";

  // Decision 4: a plan bought that starts later is the "starts soon" page, which links on to Billing. The Billing page itself stays open.
  const startDate = billingStore.oneTimePurchase?.start_date;
  const startsLater = !!startDate && new Date(startDate).getTime() > Date.now();

  // A fresh sign-in or reload lands on /pricing before the subscription answer is in, so "starts later" is not known yet: wait for it
  // (one fetch) instead of sending the Owner on to Billing (walkthrough decision 4, final round).
  const waitingForPackage = locked && hasBillingPerm && !startsLater && location.pathname === "/pricing" && billingStore.subscriptionSettled === false;
  useEffect(() => {
    if (waitingForPackage) dispatch(billingTriggerActions.getSubscription());
  }, [waitingForPackage, dispatch]);

  if (!isUser) {
    return <ToLogin />
  }

  if (waitingForPackage) return null;

  if (locked && hasBillingPerm && startsLater && location.pathname === "/pricing") {
    return <Navigate to="/package-pending" replace />
  }

  if (locked && !isAllowedPath && !isOwnerSettingsPath) {
    return <Navigate to={hasBillingPerm ? (startsLater ? "/package-pending" : "/pricing") : lockedLandingPath()} />
  }

  return children
}
